import { useMemo, useState } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import { NavArrowDown, Search, WarningTriangle } from 'iconoir-react'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { resolveLocationScope } from 'app/lib/locationScope'
import type { LocationIntegrationStatus } from 'app/hooks/useLocationIntegrationStatus'
import { OptionType } from 'types/OptionType'

/**
 * Standing statement of how much of the selected world is actually reporting.
 *
 * This is deliberately not an alert. The condition it reports is usually permanent and often
 * unfixable by the person reading it, so a dismissible amber slab just trains people to stop
 * looking. It is a caption on the data instead: which locations are not reporting, what kind
 * of problem each one has, and therefore how far to trust the numbers underneath.
 */

type AffectedLocation = {
  id: string
  name: string
  status: LocationIntegrationStatus
  lastPmsSyncDate?: string
  /** "Not connected" / "Synced Apr 12", authored once in useLocationIntegrationStatus. */
  statusLabel?: string
}

/** Joins names into readable prose: "A", "A and B", "A, B and C". */
function toSentenceList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ``
  return `${names.slice(0, -1).join(`, `)} and ${names[names.length - 1]}`
}

/** Sync dates read as "Apr 12" within the year and "Apr 12, 2025" outside it. */
function formatSyncDate(date: string): string {
  const synced = moment(date)
  return synced.isSame(moment(), `year`) ? synced.format(`MMM D`) : synced.format(`MMM D, YYYY`)
}

/** Names shown inline before the rest collapse into a count. */
const INLINE_NAME_LIMIT = 2

/** Above this many rows the list needs a search box to stay usable. */
const SEARCHABLE_AT = 10

export default function LocationCoverageNotice({
  selectedOptions,
  locationOptions,
  className,
}: {
  /** The current location filter. Empty means every location is in scope. */
  selectedOptions?: OptionType[]
  /** Every location available to this page, with `meta.integrationStatus` set. */
  locationOptions: OptionType[]
  /** Overrides the notice's spacing for a page laid out differently. */
  className?: string
}) {
  const [search, setSearch] = useState(``)

  // Shared with the location badges so the notice's total and the amber count are always the
  // same number about the same set. See resolveLocationScope for why grouping options need care.
  const scope = useMemo(
    () => resolveLocationScope(locationOptions, selectedOptions),
    [selectedOptions, locationOptions],
  )

  const affected: AffectedLocation[] = useMemo(
    () =>
      scope
        .filter((option) => option.meta?.integrationStatus !== `integrated`)
        .map((option) => ({
          id: option.id,
          name: option.name,
          status: option.meta.integrationStatus as LocationIntegrationStatus,
          lastPmsSyncDate: option.meta.lastPmsSyncDate,
          statusLabel: option.meta.statusLabel,
        })),
    [scope],
  )

  // Staleness has a magnitude, so the stale group sorts by it: the location that died ten months
  // ago leads, rather than sorting under whatever letter its name happens to start with. Being
  // unconnected has no magnitude, so that group stays alphabetical.
  const stale = useMemo(
    () =>
      affected
        .filter((location) => location.status === `missingData`)
        .sort((a, b) => (a.lastPmsSyncDate ?? ``).localeCompare(b.lastPmsSyncDate ?? ``)),
    [affected],
  )
  const disconnected = useMemo(
    () =>
      affected.filter((location) => location.status === `notIntegrated`).sort((a, b) => a.name.localeCompare(b.name)),
    [affected],
  )

  if (affected.length === 0) return null

  // A handful of locations are worth naming. Past that the names stop being information and the
  // count carries the sentence, with the list behind `All N` doing the naming.
  const subject = (group: AffectedLocation[]) =>
    group.length <= INLINE_NAME_LIMIT ? (
      <span className="font-medium text-strong">{toSentenceList(group.map((location) => location.name))}</span>
    ) : (
      <span className="font-medium text-strong">{group.length} locations</span>
    )

  // Named alone, a stale location can carry its own date. In a crowd, the oldest sync is the
  // number that matters: it is the difference between "yesterday's job slipped" and "this has
  // been broken since last year".
  const oldestSync = stale.find((location) => location.lastPmsSyncDate)?.lastPmsSyncDate
  const staleClause =
    stale.length === 1 && stale[0].lastPmsSyncDate ? (
      <>
        last synced <span className="font-medium text-strong">{formatSyncDate(stale[0].lastPmsSyncDate)}</span>
      </>
    ) : stale.length > INLINE_NAME_LIMIT && oldestSync ? (
      <>
        have not synced recently, the oldest since{' '}
        <span className="font-medium text-strong">{formatSyncDate(oldestSync)}</span>
      </>
    ) : (
      `${stale.length === 1 ? `has` : `have`} not synced recently`
    )

  /** One cause, described the way the notice's list and the dropdown rows describe it. */
  const causeText = (group: AffectedLocation[], cause: `disconnected` | `stale`) => {
    // The headline already established that these are locations, so a collapsed count stands
    // alone here: "31 not connected" rather than "31 locations not connected".
    const named = group.length <= INLINE_NAME_LIMIT
    const who = named ? toSentenceList(group.map((location) => location.name)) : `${group.length}`
    if (cause === `disconnected`) return `${who} not connected`
    if (named) {
      return group.length === 1 && group[0].lastPmsSyncDate
        ? `${who}, synced ${formatSyncDate(group[0].lastPmsSyncDate)}`
        : `${who} with stale data`
    }
    return `${who} with stale data`
  }

  // Split by cause. Lumping a disconnected location together with a stale one under a single
  // "not integrated" was the old banner's central lie: the two mean different things about the
  // numbers, and only one of them is a setup that never happened.
  //
  // With one cause a plain sentence is the clearest thing there is. With two, that sentence turns
  // into arithmetic the reader has to perform ("37 ... and 31 ..." to reach the 68 in the badge),
  // so the claim leads and the causes follow as scannable, colour-anchored fragments.
  //
  // None of these spell out the consequence ("your totals are understated"). The amber framing
  // and "not reporting" carry it, and saying it outright made every variant a sentence longer.
  const headline =
    stale.length === 0 ? (
      <>
        {subject(disconnected)} {disconnected.length === 1 ? `is` : `are`} not connected to a PMS.
      </>
    ) : disconnected.length === 0 ? (
      <>
        {subject(stale)} {staleClause}.
      </>
    ) : (
      <>
        <span className="font-medium text-strong">
          {affected.length} of {scope.length} locations
        </span>{' '}
        are not reporting complete data.
      </>
    )

  const causes =
    stale.length > 0 && disconnected.length > 0
      ? [
          { key: `disconnected`, dot: `bg-red-600`, text: causeText(disconnected, `disconnected`) },
          { key: `stale`, dot: `bg-yellow-600`, text: causeText(stale, `stale`) },
        ]
      : []

  // The list only earns its place when it says something the sentence could not: locations the
  // sentence had to collapse into a count, or sync dates behind a stale location.
  const showList = disconnected.length > INLINE_NAME_LIMIT || stale.length > 0
  const searchable = affected.length > SEARCHABLE_AT
  const query = search.trim().toLowerCase()
  // Each group owns one colour and one meaning, so its rows do not have to repeat either. Only
  // the stale group has anything left to say per row, because its dates differ.
  const groups = [
    { key: `stale`, label: `Stale data`, dot: `bg-yellow-600`, showRowStatus: true, all: stale },
    { key: `disconnected`, label: `Not connected`, dot: `bg-red-600`, showRowStatus: false, all: disconnected },
  ]
    .map((group) => ({
      ...group,
      // The heading keeps the true group total; only the rows narrow, so filtering to one match
      // out of forty never reads as "there were only ever two problems here".
      rows: query ? group.all.filter((location) => location.name.toLowerCase().includes(query)) : group.all,
    }))
    .filter((group) => group.all.length > 0)
  const noMatches = groups.every((group) => group.rows.length === 0)

  return (
    <div
      className={classNames(
        className ?? `mb-6`,
        `flex w-full items-start gap-3 rounded-md border border-amber-300 bg-surface-raised py-2.5 pl-3 pr-2.5 shadow-elevation-1`,
      )}
    >
      <WarningTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" strokeWidth={2} />
      {/* One wrapping line: the claim, then the causes as colour-anchored fragments. They flow
          as part of the same line and only drop to a second row when the width runs out. */}
      <p className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-body-sm leading-5 text-default">
        <span>{headline}</span>
        {causes.map((cause) => (
          <span key={cause.key} className="flex items-center gap-1.5 text-subtle">
            <span className={classNames(`h-1.5 w-1.5 shrink-0 rounded-full`, cause.dot)} />
            {cause.text}
          </span>
        ))}
      </p>
      {showList && (
        <Popover onOpenChange={(open) => !open && setSearch(``)}>
          {/* "All 68" never said 68 of what. The trigger now names the thing it opens. */}
          <PopoverTrigger className="-my-0.5 flex shrink-0 items-center gap-1 rounded px-2 py-1 text-ui-xs font-medium text-default transition-colors hover:bg-surface-subtle hover:text-strong focus:outline-none focus-visible:ring-1 focus-visible:ring-strong data-[state=open]:bg-surface-subtle">
            {affected.length === 1 ? `View location` : `View ${affected.length} locations`}
            <NavArrowDown className="h-3.5 w-3.5 text-minimal" strokeWidth={2} />
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-[27rem] max-w-[calc(100vw-2rem)] gap-0 rounded-lg border border-default bg-surface-raised p-0 shadow-elevation-4 ring-0"
          >
            <div className="border-b border-subtle px-3 py-2.5">
              <p className="text-body-sm font-medium text-strong">Locations not reporting</p>
              <p className="mt-0.5 text-ui-xs text-subtle">Their data is missing from the numbers on this page.</p>
            </div>
            {searchable && (
              <div className="flex items-center gap-2 border-b border-subtle px-3 py-2">
                <Search className="h-3.5 w-3.5 shrink-0 text-minimal" strokeWidth={2} />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={`Search ${affected.length} locations`}
                  className="w-full bg-transparent text-body-sm text-strong placeholder:text-minimal focus:outline-none"
                />
              </div>
            )}
            <div className="max-h-80 overflow-y-auto">
              {noMatches && <p className="px-3 py-4 text-center text-body-sm text-subtle">No locations match.</p>}
              {groups.map((group) => (
                <div key={group.key}>
                  <div className="sticky top-0 flex items-center gap-2 border-b border-subtle bg-surface-raised px-3 py-1.5">
                    <span className={classNames(`h-1.5 w-1.5 shrink-0 rounded-full`, group.dot)} />
                    <span className="text-ui-xs font-medium text-default">{group.label}</span>
                    <span className="text-ui-xs text-minimal">{group.all.length}</span>
                  </div>
                  <ul className="py-1">
                    {group.rows.map((location) => (
                      <li key={location.id} className="flex items-center justify-between gap-3 px-3 py-1.5">
                        <span className="truncate text-body-sm text-strong" title={location.name}>
                          {location.name}
                        </span>
                        {group.showRowStatus && (
                          <span className="shrink-0 text-ui-xs text-subtle">{location.statusLabel}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
